/**
 * PropertyTable – desktop table view for property list
 * List card scrolls horizontally when the viewport is narrower than the table min width.
 */
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import type { Property, PropertyTableProps } from '../types'
import { PropertyTableSortColumnHeader } from './PropertyTableSortHeader'
import { useUserRole } from '@/hooks/useUserRole'
import { formatPhoneDisplay } from '../utils/formatPhone'
import { formatPafSummaryDate } from '@/features/company/utils/pafDateFormat'
import { cn } from '@/lib/utils/helpers'
import styles from '@/features/invitee/styles/invitesParity.module.css'
import '../styles/propertyTable.css'

function formatAddress(p: Property): string {
  const parts = [
    p.property_address,
    p.property_address2,
    [p.property_city, p.property_state, p.property_zipcode].filter(Boolean).join(', '),
  ].filter(Boolean)
  return parts.join(' ')
}

/** Relative column weights; normalized to 100% in the colgroup */
function buildPropertyColWeights(isLiberty: boolean): number[] {
  const w: number[] = [12, 4, 9, 16, 6, 8, 6, 6, 5.5]
  if (isLiberty) w.push(5)
  w.push(6) /* Liberty Listed */
  if (isLiberty) w.push(6) /* Deleted — wide enough for MM/dd/yyyy without wrap */
  return w
}

function colWidthsFromWeights(weights: number[]) {
  const s = weights.reduce((a, b) => a + b, 0)
  return weights.map((n) => `${(n * 100) / s}%`)
}

export function PropertyTable({
  properties,
  onSort,
  sortBy,
  orderBy,
  onToggleListed,
  loading = false,
}: PropertyTableProps) {
  const { isLibertyRole, hasPolicy, hasWriteAccess } = useUserRole()
  const canEditListed =
    hasPolicy(['property_listed_edit']) && hasWriteAccess('property')

  const colWidths = useMemo(
    () => colWidthsFromWeights(buildPropertyColWeights(isLibertyRole)),
    [isLibertyRole]
  )
  const colCount = colWidths.length

  return (
    <div data-testid="property-list-table-card" className={styles.tableCard}>
      <div
        data-testid="property-list-table-scroll"
        className={cn(styles.tableResponsiveScroll, 'touch-scroll')}
      >
        <table
          data-testid="property-table"
          className={cn(
            styles.listingTable,
            styles.propertyListingTable,
            'table-fixed w-full min-w-[85rem] !max-w-none [&_thead_th]:!whitespace-nowrap [&_tbody_td:not([colspan])]:!whitespace-normal',
            '[&_tbody_td:not([colspan])]:break-words',
            '[&_thead_th]:!px-2.5 [&_thead_th]:!py-2 [&_thead_th]:!align-middle',
            '[&_tbody_td]:!px-2.5 [&_tbody_td]:!py-2 [&_tbody_td]:!text-[13px] [&_tbody_td]:!leading-snug',
          )}
        >
          <colgroup>
            {colWidths.map((width, i) => (
              <col key={i} style={{ width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <PropertyTableSortColumnHeader field="property_name" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
                Property Name
              </PropertyTableSortColumnHeader>
              <PropertyTableSortColumnHeader align="center" field="property_id" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
                ID
              </PropertyTableSortColumnHeader>
              <PropertyTableSortColumnHeader field="company_name" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
                Company
              </PropertyTableSortColumnHeader>
              <PropertyTableSortColumnHeader field="property_address" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
                Address
              </PropertyTableSortColumnHeader>
              <PropertyTableSortColumnHeader field="contact_name" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
                Manager
              </PropertyTableSortColumnHeader>
              <PropertyTableSortColumnHeader field="contact_email" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
                Manager Email
              </PropertyTableSortColumnHeader>
              <PropertyTableSortColumnHeader field="contact_phone" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
                Manager Phone
              </PropertyTableSortColumnHeader>
              <PropertyTableSortColumnHeader
                align="center"
                field="occupancy"
                orderBy={orderBy}
                sortBy={sortBy}
                onSort={onSort}
              >
                Occupancy
              </PropertyTableSortColumnHeader>
              <PropertyTableSortColumnHeader
                align="center"
                field="units_count"
                orderBy={orderBy}
                sortBy={sortBy}
                onSort={onSort}
              >
                Unit Count
              </PropertyTableSortColumnHeader>
              {isLibertyRole && (
                <PropertyTableSortColumnHeader field="property_alias_name" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
                  Alias Name
                </PropertyTableSortColumnHeader>
              )}
              <th
                className="!text-center !align-middle"
                data-testid="property-table-header-liberty-listed"
              >
                Liberty Listed
              </th>
              {isLibertyRole && (
                <PropertyTableSortColumnHeader align="center" field="deleted_at" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
                  Deleted
                </PropertyTableSortColumnHeader>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr data-testid="property-table-loading-row">
                <td colSpan={colCount} className="!py-16">
                  <div className="flex items-center justify-center">
                    <Loader2
                      className="h-8 w-8 animate-spin text-[rgb(21_151_255)]"
                      data-testid="property-list-loading"
                    />
                  </div>
                </td>
              </tr>
            ) : properties.length === 0 ? (
              <tr data-testid="property-table-empty-row">
                <td colSpan={colCount} className="!py-12 text-center text-slate-500">
                  No properties found.
                </td>
              </tr>
            ) : (
              properties.map((property) => (
              <tr
                key={property.property_id}
                data-testid={`property-table-row-${property.property_id}`}
                className="transition-colors"
              >
                <td>
                  <Link
                    data-testid={`property-table-row-${property.property_id}-name-link`}
                    to={`/property/edit/${property.property_id}`}
                    className="text-[13px] font-medium text-[rgb(21_151_255)] hover:text-[#0d7acc] hover:underline"
                  >
                    {property.property_name}
                  </Link>
                </td>
                <td className="!text-center text-slate-600">{property.property_id}</td>
                <td className="text-slate-600">{property.company_name ?? '—'}</td>
                <td className="text-slate-600" title={formatAddress(property)}>
                  {formatAddress(property) || '—'}
                </td>
                <td className="text-slate-600">{property.contact_name ?? '—'}</td>
                <td className="text-slate-600 break-all">{property.contact_email ?? '—'}</td>
                <td className="text-slate-600">{formatPhoneDisplay(property.contact_phone) || '—'}</td>
                <td className="!text-center text-slate-600 tabular-nums">{property.occupancy ?? '—'}</td>
                <td className="!text-center text-slate-600 tabular-nums">{property.units_count ?? '—'}</td>
                {isLibertyRole && <td className="text-slate-600">{property.property_alias_name ?? '—'}</td>}
                <td className="!text-center">
                  <button
                    data-testid={`property-table-row-${property.property_id}-listed-button`}
                    onClick={() => onToggleListed?.(property)}
                    disabled={!canEditListed}
                    className={cn(
                      'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold transition-all',
                      property.org_listed_ids?.['1'] === 1
                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                        : 'bg-red-100 text-red-700 hover:bg-red-200',
                      !canEditListed ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                    )}
                    type="button"
                  >
                    {property.org_listed_ids?.['1'] === 1 ? 'Active' : 'Not Active'}
                  </button>
                </td>
                {isLibertyRole && (
                  <td className="!text-center text-slate-600 whitespace-nowrap">
                    {property.deleted_at
                      ? formatPafSummaryDate(property.deleted_at) || ''
                      : ''}
                  </td>
                )}
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
